/**
 * REQUEST DEDUPLICATION SERVICE
 * 
 * Prevents duplicate API requests from being sent simultaneously.
 * Uses AbortController to cancel previous pending requests.
 * 
 * Features:
 * - Automatic deduplication of identical GET requests
 * - Configurable deduplication window
 * - Request fingerprinting (method + url + params)
 * - Debug logging
 * - Zero config needed - works out of the box
 */

import type { InternalAxiosRequestConfig } from 'axios';

interface PendingRequest {
  controller: AbortController;
  timestamp: number;
  url: string;
  method: string;
}

// Active pending requests map
const pendingRequests = new Map<string, PendingRequest>();

// Config
const DEDUPLICATION_WINDOW_MS = 500; // 500ms window for deduplication
const ENABLE_DEBUG_LOGGING = import.meta.env.VITE_ENABLE_REQUEST_DEDUP_DEBUG === 'true';

/**
 * Generate unique key for a request
 */
function generateRequestKey(config: InternalAxiosRequestConfig): string {
  const { method, url, params, data } = config;
  
  // For GET requests: method + url + params
  if (method?.toLowerCase() === 'get') {
    const paramsKey = params ? JSON.stringify(params) : '';
    return `${method}:${url}:${paramsKey}`;
  }
  
  // For POST/PUT/PATCH: method + url + body
  // NOTE: We don't deduplicate mutations by default (can be dangerous)
  // Only deduplicate if explicitly enabled
  if (config.headers?.['X-Deduplicate'] === 'true') {
    const bodyKey = data ? JSON.stringify(data) : '';
    return `${method}:${url}:${bodyKey}`;
  }
  
  // Don't deduplicate by default for mutations
  return `${method}:${url}:${Date.now()}:${Math.random()}`;
}

/**
 * Check if we should deduplicate this request
 */
function shouldDeduplicate(config: InternalAxiosRequestConfig): boolean {
  // Always deduplicate GET requests
  if (config.method?.toLowerCase() === 'get') {
    return true;
  }
  
  // Only deduplicate mutations if explicitly enabled
  if (config.headers?.['X-Deduplicate'] === 'true') {
    return true;
  }
  
  return false;
}

/**
 * Add request to pending map and get AbortController
 */
export function addPendingRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (!shouldDeduplicate(config)) {
    return config;
  }
  
  const requestKey = generateRequestKey(config);
  const existingRequest = pendingRequests.get(requestKey);
  
  // Check if there's a pending request within the deduplication window
  if (existingRequest) {
    const timeSinceRequest = Date.now() - existingRequest.timestamp;
    
    if (timeSinceRequest < DEDUPLICATION_WINDOW_MS) {
      if (ENABLE_DEBUG_LOGGING) {
        console.log(`[Request Dedup] 🚫 Duplicate request detected:`, {
          method: config.method,
          url: config.url,
          timeSinceOriginal: `${timeSinceRequest}ms`,
        });
      }
      
      // Cancel the previous request
      existingRequest.controller.abort('Duplicate request - newer request initiated');
    }
  }
  
  // Create new AbortController for this request
  const controller = new AbortController();
  config.signal = controller.signal;
  
  // Store this request
  pendingRequests.set(requestKey, {
    controller,
    timestamp: Date.now(),
    url: config.url || '',
    method: config.method || '',
  });
  
  if (ENABLE_DEBUG_LOGGING) {
    console.log(`[Request Dedup] ✅ Request added to queue:`, {
      method: config.method,
      url: config.url,
      totalPending: pendingRequests.size,
    });
  }
  
  return config;
}

/**
 * Remove request from pending map.
 *
 * Signal-guarded: only deletes the map entry if the stored controller's signal
 * matches this request's signal. This prevents a stale aborted request's
 * cleanup from evicting a newer request that took over the same dedup key.
 */
export function removePendingRequest(config: InternalAxiosRequestConfig): void {
  if (!config.method || !config.url) return;

  const requestKey = generateRequestKey(config);
  const pending = pendingRequests.get(requestKey);

  // Guard: only remove if this request owns the current map slot.
  // If the slot belongs to a newer request (abort replaced the entry),
  // its signal will differ from config.signal — skip deletion.
  if (pending && pending.controller.signal === config.signal) {
    pendingRequests.delete(requestKey);
    if (ENABLE_DEBUG_LOGGING) {
      console.log(`[Request Dedup] ✓ Request completed:`, {
        method: config.method,
        url: config.url,
        remainingPending: pendingRequests.size,
      });
    }
  }
}

/**
 * Clear all pending requests (useful on logout/navigation)
 */
export function clearAllPendingRequests(): void {
  if (ENABLE_DEBUG_LOGGING) {
    console.log(`[Request Dedup] 🧹 Clearing ${pendingRequests.size} pending requests`);
  }
  
  // Cancel all pending requests
  pendingRequests.forEach((request) => {
    request.controller.abort('Clearing all pending requests');
  });
  
  pendingRequests.clear();
}

/**
 * Get stats about pending requests (for debugging/monitoring)
 */
export function getPendingRequestsStats() {
  return {
    total: pendingRequests.size,
    requests: Array.from(pendingRequests.values()).map(req => ({
      method: req.method,
      url: req.url,
      age: Date.now() - req.timestamp,
    })),
  };
}

/**
 * Enable deduplication for a specific mutation
 * Usage: api.post('/endpoint', data, { headers: { 'X-Deduplicate': 'true' } })
 */
export function enableDeduplicationForRequest(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  config.headers = config.headers || {};
  config.headers['X-Deduplicate'] = 'true';
  return config;
}
